import { usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '@repo/backend';
import type { Id } from '../../../../packages/backend/convex/_generated/dataModel';
import type { AuctionListItem, AuctionDetailItem } from '@/data/auction';

// ─── Adapters ─────────────────────────────────────────────────────────────────

type ConvexAuctionListEntry = {
  auction: {
    _id: Id<'auctions'>;
    cardId: Id<'cards'>;
    sellerId: Id<'users'>;
    status: 'pending' | 'open' | 'closed' | 'cancelled';
    startingPrice: number;
    currentBid: number;
    bidCount: number;
    endsAt: number;
    currentBidderId?: Id<'users'>;
  };
  card: {
    name: string;
    rarity: string;
    cardType: string;
    imageUrl?: string;
    flavorText?: string;
  } | null | undefined;
  minNextBid: number;
};

/** Map a Convex auction+card pair to the shape the existing UI components expect. */
export function toAuctionListItem(entry: ConvexAuctionListEntry): AuctionListItem {
  return {
    id: entry.auction._id,
    name: entry.card?.name ?? 'Unknown Card',
    edition: entry.card
      ? `${capitalize(entry.card.rarity)} • ${capitalize(entry.card.cardType)}`
      : '',
    image: entry.card?.imageUrl ?? '',
    currentHighest: entry.auction.currentBid,
    totalBids: entry.auction.bidCount,
    endsAt: entry.auction.endsAt,
    rarity: capitalize(entry.card?.rarity ?? 'common'),
  };
}

/** Map a Convex auction detail to the shape the existing UI components expect. */
export function toAuctionDetailItem(
  entry: ConvexAuctionListEntry & {
    auction: { startingPrice: number };
    card: NonNullable<ConvexAuctionListEntry['card']> & { description: string };
  },
  sellerName: string,
): AuctionDetailItem {
  return {
    ...toAuctionListItem(entry),
    startBid: entry.auction.startingPrice,
    owner: sellerName,
    ownerTitle: 'Keeper of the Collection',
    description: entry.card.description,
  };
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Paginated list of open auctions with card data joined. */
export function useOpenAuctions(initialNumItems = 20) {
  return usePaginatedQuery(
    api.auctions.queries.listOpen,
    {},
    { initialNumItems },
  );
}

/** Single auction + card detail. Returns undefined while loading. */
export function useAuctionDetail(auctionId: string) {
  return useQuery(api.auctions.queries.getById, {
    auctionId: auctionId as Id<'auctions'>,
  });
}

/** Paginated bid history for an auction, newest first. */
export function useBidHistory(auctionId: string, initialNumItems = 10) {
  return usePaginatedQuery(
    api.bids.queries.listByAuction,
    { auctionId: auctionId as Id<'auctions'> },
    { initialNumItems },
  );
}
